import { createSignal, createEffect, onCleanup, untrack, Show } from "solid-js";
import { useApi, isOk } from "../api";
import { useTranslation } from "../hooks/useTranslation";
import Text from "./Text";
import { EmptyState } from "./EmptyState";
import type { ProxyHealthBucketDto } from "../api/generated/schemas";
import {
    Chart,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Filler,
    Tooltip,
} from "chart.js";
import "chartjs-adapter-moment";

Chart.register(TimeScale, LinearScale, PointElement, LineElement, LineController, Filler, Tooltip);

interface Props {
    proxyId: string;
}

const RANGES: { key: string; days: number }[] = [
    { key: "PROXY_HEALTH_RANGE_24H", days: 1 },
    { key: "PROXY_HEALTH_RANGE_7D", days: 7 },
    { key: "PROXY_HEALTH_RANGE_30D", days: 30 },
];

export default function ProxyHealthChart(props: Props) {
    const api = useApi();
    const { t } = useTranslation();

    const [buckets, setBuckets] = createSignal<ProxyHealthBucketDto[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [days, setDays] = createSignal(1);

    let canvas: HTMLCanvasElement | undefined;
    let chart: Chart | undefined;

    async function fetchHistory(proxyId: string, rangeDays: number) {
        setLoading(true);
        try {
            const to = new Date();
            const from = new Date(to.getTime() - rangeDays * 24 * 60 * 60 * 1000);
            const res = await api.proxy.getHealth(proxyId, { from: from.toISOString(), to: to.toISOString() });
            if (isOk(res.data)) setBuckets((res.data.value.buckets ?? []) as ProxyHealthBucketDto[]);
            else setBuckets([]);
        } finally {
            setLoading(false);
        }
    }

    createEffect(() => {
        const id = props.proxyId;
        const d = days();
        untrack(() => void fetchHistory(id, d));
    });

    createEffect(() => {
        const data = buckets();
        untrack(() => { if (canvas && canvas.isConnected) renderChart(data); });
    });

    function mountCanvas(el: HTMLCanvasElement) {
        canvas = el;
        queueMicrotask(() => { if (el.isConnected) renderChart(buckets()); });
    }

    function renderChart(data: ProxyHealthBucketDto[]) {
        if (!canvas) return;
        if (chart) { chart.destroy(); chart = undefined; }
        if (data.length === 0) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        chart = new Chart(ctx, {
            type: "line",
            data: {
                datasets: [{
                    label: t("PROXY_HEALTH_PING"),
                    data: data.map(b => ({ x: new Date(b.t).toISOString(), y: b.avg ?? null })) as any,
                    borderColor: "#7c4dff",
                    backgroundColor: "#7c4dff22",
                    borderWidth: 2,
                    fill: true,
                    tension: 0.2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    spanGaps: false,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                interaction: { mode: "nearest", intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: "rgba(30, 30, 30, 0.95)",
                        titleColor: "#ffffff",
                        bodyColor: "#e0e0e0",
                        borderColor: "#444444",
                        borderWidth: 1,
                        callbacks: {
                            label: (item) => {
                                const b = data[item.dataIndex];
                                if (!b || b.avg == null) return t("PROXY_HEALTH_FAILURES");
                                const range = `${t("PROXY_HEALTH_RANGE_MINMAX")}: ${Math.round(b.min ?? 0)}-${Math.round(b.max ?? 0)} ms`;
                                return `${t("PROXY_HEALTH_PING")}: ${Math.round(b.avg)} ms (${range})`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        type: "time",
                        time: { tooltipFormat: "DD.MM.YYYY, HH:mm" },
                        ticks: { color: "#999999", maxRotation: 0, autoSkipPadding: 16 },
                        grid: { color: "rgba(255, 255, 255, 0.05)" },
                    },
                    y: {
                        beginAtZero: true,
                        ticks: { color: "#999999", callback: (v) => `${v} ms` },
                        grid: { color: "rgba(255, 255, 255, 0.05)" },
                    },
                },
            },
        });
    }

    onCleanup(() => { if (chart) chart.destroy(); });

    return (
        <div class="v" style={{ gap: "var(--spacing-md)" }}>
            <div class="h" style={{ "justify-content": "space-between", "align-items": "center" }}>
                <h3 style={{ margin: 0, "font-size": "var(--font-size-lg)" }}><Text val="PROXY_HEALTH_TITLE" /></h3>
                <div class="h" style={{ gap: "var(--spacing-xs)" }}>
                    {RANGES.map(r => (
                        <button
                            class={`btn btn-sm ${days() === r.days ? "btn-primary" : "btn-secondary"}`}
                            onClick={() => setDays(r.days)}
                        >
                            <Text val={r.key} />
                        </button>
                    ))}
                </div>
            </div>

            <Show
                when={!loading()}
                fallback={
                    <div class="hcenter" style={{ "justify-content": "center", height: "16rem", color: "var(--dark-text-muted)" }}>
                        <i class="fa-solid fa-spinner fa-spin fa-2x" />
                    </div>
                }
            >
                <Show
                    when={buckets().length > 0}
                    fallback={<EmptyState icon="fa-chart-line" title={t("PROXY_HEALTH_EMPTY")} />}
                >
                    <div style={{ position: "relative", height: "16rem", width: "100%" }}>
                        <canvas ref={mountCanvas} />
                    </div>
                </Show>
            </Show>
        </div>
    );
}
